import React, { Component } from "react";
import { injectIntl } from "react-intl";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import { DatePicker, Radio, Progress, Icon, Tooltip } from "antd";
import { v4 as uuidv4 } from "uuid";
import { sendNotification } from "../../utils";
import UploadFile from "../../utils/UploadFile";
import "moment/locale/es-us";
import moment from "moment";
import "./Copyright.css";
import {
  RIGHTS_STATUS_UNASSIGNED,
  RIGHTS_STATUS_NOT_REQUIRED,
  RIGHTS_STATUS_ENABLED,
  LICENSE_FILE_PREFIX,
  LICENSE_FILE_EXTENSIONS,
  LICENSE_FILE_MAX_BYTES
} from "../../constants/rights-constants";

// Estados de la UI del selector (mockup "Pantallas de Derechos de Uso", pin 9).
// No confundir con `rightsStatus`, el campo que persiste el backend: "sin
// vencimiento" y "con vencimiento" son ambos rightsStatus=enabled, la
// diferencia entre esos dos la da si dueDate está seteado o no.
const UI_UNASSIGNED = "unassigned";
const UI_NOT_REQUIRED = "not_required";
const UI_NO_DUE_DATE = "no_due_date";
const UI_DUE_DATE = "due_date";
// Lo necesita quien embebe el widget para exigir la fecha antes de guardar.
export const COPYRIGHT_UI_DUE_DATE = UI_DUE_DATE;

function deriveUiState(rightsStatus, dueDate) {
  if (rightsStatus === RIGHTS_STATUS_NOT_REQUIRED) return UI_NOT_REQUIRED;
  if (rightsStatus !== RIGHTS_STATUS_ENABLED) return UI_UNASSIGNED;
  return dueDate ? UI_DUE_DATE : UI_NO_DUE_DATE;
}

/** El estado persistido que corresponde a cada opción de la UI. */
function statusForUiState(uiState) {
  if (uiState === UI_NOT_REQUIRED) return RIGHTS_STATUS_NOT_REQUIRED;
  if (uiState === UI_UNASSIGNED) return RIGHTS_STATUS_UNASSIGNED;
  return RIGHTS_STATUS_ENABLED;
}

function rightsModuleActive(modules) {
  return (modules || []).some(
    (module) => module.name === "useRights" && module.activate
  );
}

/**
 * Versión simple (cuentas sin el módulo, DES-216): sólo dos opciones,
 * "Indefinido" o un período. La decide la fecha de fin y no rightsStatus:
 * los archivos de antes de DES-196 tienen sus fechas sin rightsStatus y
 * tienen que verse con su período, no como indefinidos.
 */
function deriveSimpleUiState(dueDate) {
  return dueDate ? UI_DUE_DATE : UI_UNASSIGNED;
}

export class Copyright extends Component {
  constructor(props) {
    super(props);

    const { fillRightsStatus, fillCreateDate, fillDueDate, fillUiState } = props;

    this.state = {
      createDate: fillCreateDate || moment().format("YYYY-MM-DD"),
      dueDate: fillDueDate || null,
      uiState: fillUiState || this.deriveUi(fillRightsStatus, fillDueDate),
      // Contrato que respalda el período. `licenseTouched` distingue "no toqué
      // el contrato" de "lo quité": sin esa diferencia, asignar derechos a un
      // lote sin adjuntar nada mandaría licenseFile:null y borraría el contrato
      // que esos assets ya tuvieran.
      licenseFile: props.fillLicenseFile || null,
      licenseFileName: props.fillLicenseFileName || null,
      licenseTouched: false,
      licenseUploading: false,
      licenseProgress: 0
    };

    this.licenseInputRef = React.createRef();
  }

  componentDidMount() {
    const { fillRightsStatus, fillCreateDate, fillDueDate, fillUiState } = this.props;

    if (fillCreateDate || fillDueDate || fillRightsStatus) {
      this.setState({
        createDate: fillCreateDate || moment().format("YYYY-MM-DD"),
        dueDate: fillDueDate || null,
        uiState: fillUiState || this.deriveUi(fillRightsStatus, fillDueDate)
      });
    }
  }

  deriveUi(rightsStatus, dueDate) {
    return this.esVersionSimple()
      ? deriveSimpleUiState(dueDate)
      : deriveUiState(rightsStatus, dueDate);
  }

  /**
   * Sin el módulo de Derechos de Uso, el widget muestra la versión simple
   * (DES-216): "Indefinido" o un período de inicio y fin, sin contrato.
   *
   * La decisión va acá y no en cada llamador porque son siete. `modules`
   * llega por store en la versión conectada (la que usan seis de los siete) y
   * por prop en la versión sin conectar, que es la del panel del asset
   * (CollectionMiddleViewAssetInfo importa el named export). Si ahí no se
   * pasara, el panel mostraría la versión simple justo donde el módulo SÍ
   * está activo.
   */
  esVersionSimple() {
    return !rightsModuleActive(this.props.modules);
  }

  componentDidUpdate(prevProps) {
    // El caller no le pone `key` a este componente por asset (ver
    // CollectionMiddleViewAssetInfo.js): al cambiar de asset seleccionado, la
    // misma instancia sigue montada y sólo cambian las props. Sin esto, el
    // widget seguía mostrando los derechos del asset anterior.
    const {
      fillRightsStatus,
      fillCreateDate,
      fillDueDate,
      fillUiState,
      fillLicenseFile,
      fillLicenseFileName
    } = this.props;
    if (
      prevProps.fillRightsStatus !== fillRightsStatus ||
      prevProps.fillCreateDate !== fillCreateDate ||
      prevProps.fillDueDate !== fillDueDate ||
      prevProps.fillUiState !== fillUiState ||
      prevProps.fillLicenseFile !== fillLicenseFile
    ) {
      this.setState({
        createDate: fillCreateDate || moment().format("YYYY-MM-DD"),
        dueDate: fillDueDate || null,
        uiState: fillUiState || this.deriveUi(fillRightsStatus, fillDueDate),
        // Al cambiar de asset el contrato vuelve a "no tocado": lo que se vea
        // es lo que ese asset tiene guardado, no el resto de la edición anterior.
        licenseFile: fillLicenseFile || null,
        licenseFileName: fillLicenseFileName || null,
        licenseTouched: false,
        licenseUploading: false,
        licenseProgress: 0
      });
    }
  }

  emitChange = (uiState, createDate, dueDate) => {
    if (this.esVersionSimple()) {
      this.emitSimpleChange(uiState, createDate, dueDate);
      return;
    }

    const { licenseFile, licenseTouched } = this.state;

    const copyright = {
      rightsStatus: statusForUiState(uiState),
      createDate,
      dueDate: uiState === UI_DUE_DATE ? dueDate : null,
      uiState,
      // "Con vencimiento" sin fecha de término: el backend lo guardaría como
      // "sin vencimiento" sin avisar (Ricardo, 2026-10-07). Quien recibe esto
      // no deja guardar mientras venga marcado.
      incomplete: uiState === UI_DUE_DATE && !dueDate
    };

    // La clave viaja SÓLO si el usuario tocó el selector. El backend distingue
    // "no vino la clave" (no toca el campo) de "vino en null" (lo borra), así
    // que mandarla siempre pisaría contratos ajenos a esta edición.
    if (licenseTouched) {
      copyright.licenseFile = uiState === UI_DUE_DATE ? licenseFile : null;
      // El nombre con que se subió: la key es un uuid (Ricardo, 2026-10-08).
      copyright.licenseFileName = copyright.licenseFile
        ? this.state.licenseFileName
        : null;
    }

    this.props.handleCopyright({ copyright });
  };

  /**
   * Lo que emite la versión simple. Un período a medias (sin fecha de fin)
   * sale marcado `incomplete`, igual que con el módulo, y quien lo recibe no
   * deja guardar: el backend lo guardaría como "Indefinido" sin decir nada.
   * Antes no se emitía, y el llamador se quedaba con lo último emitido:
   * guardar mandaba otra opción que la que se veía marcada.
   *
   * "Indefinido" limpia también el inicio, igual que el backend
   * (RightsService.buildSimpleRights).
   */
  emitSimpleChange = (uiState, createDate, dueDate) => {
    const conPeriodo = uiState === UI_DUE_DATE;

    this.props.handleCopyright({
      copyright: {
        rightsStatus: conPeriodo
          ? RIGHTS_STATUS_ENABLED
          : RIGHTS_STATUS_UNASSIGNED,
        createDate: conPeriodo ? createDate : null,
        dueDate: conPeriodo ? dueDate : null,
        uiState,
        incomplete: conPeriodo && !(createDate && dueDate)
      }
    });
  };

  handleOnChangeCreateDate = (date, dateString) => {
    const { dueDate, uiState } = this.state;
    this.setState({ createDate: dateString }, () => {
      this.emitChange(uiState, dateString, dueDate);
    });
  };

  handleOnChangeDueDate = (date, dateString) => {
    const { createDate, uiState } = this.state;
    if (
      date &&
      createDate &&
      date.isBefore(moment(createDate, "YYYY-MM-DD"), "day")
    ) {
      sendNotification(
        "warning",
        this.props.intl,
        this.props.intl.formatMessage({ id: "upload-folder-s3-date-incorrect" })
      );
      return;
    }
    this.setState({ dueDate: dateString }, () => {
      this.emitChange(uiState, createDate, dateString);
    });
  };

  handleClickLicense = () => {
    if (this.licenseInputRef.current) this.licenseInputRef.current.click();
  };

  handleSelectLicenseFile = e => {
    const { intl, clientId, clientName, user } = this.props;
    const picked = e.target.files && e.target.files[0];
    // El input se limpia siempre para que volver a elegir el mismo archivo
    // dispare el change de nuevo.
    e.target.value = "";
    if (!picked) return;

    // Este componente se usa en dos formas: conectado (default export) y sin
    // conectar (named export, en CollectionMiddleViewAssetInfo). Sin store no
    // hay clientId y la subida iría a una key mal formada que el backend
    // rechazaría después; es mejor cortar acá con un mensaje.
    if (!clientId) {
      sendNotification(
        "error",
        intl,
        intl.formatMessage({ id: "rights_license_upload_error" })
      );
      return;
    }

    const ext = String(picked.name.split(".").pop() || "").toLowerCase();

    if (LICENSE_FILE_EXTENSIONS.indexOf(ext) === -1) {
      sendNotification(
        "error",
        intl,
        intl.formatMessage(
          { id: "rights_license_invalid_type" },
          { formats: LICENSE_FILE_EXTENSIONS.join(", ") }
        )
      );
      return;
    }

    if (picked.size > LICENSE_FILE_MAX_BYTES) {
      sendNotification(
        "error",
        intl,
        intl.formatMessage(
          { id: "rights_license_too_large" },
          { size: Math.round(LICENSE_FILE_MAX_BYTES / (1024 * 1024)) }
        )
      );
      return;
    }

    const uuid = uuidv4();
    const file = {
      uuid,
      s3Name: `${uuid}.${ext}`,
      ext,
      applyRkg: false,
      file: picked,
      name: picked.name,
      size: picked.size,
      type: picked.type
    };

    this.setState({
      licenseUploading: true,
      licenseProgress: 0,
      licenseFileName: picked.name
    });

    const uploader = new UploadFile(clientId, clientName, user);
    uploader
      .checkIsSetAccelerateConfiguration()
      .then(() =>
        uploader.startUpload(
          file,
          null,
          (_f, progress) =>
            this.setState({
              licenseProgress: Math.round(
                (progress.loaded / progress.total) * 100
              )
            }),
          uploaded => this.onLicenseUploaded(uploaded),
          () => this.onLicenseUploadError(),
          () => this.onLicenseUploadError(),
          { keyPrefix: LICENSE_FILE_PREFIX, publicRead: false }
        )
      )
      .catch(() => this.onLicenseUploadError());
  };

  onLicenseUploaded = uploaded => {
    const { createDate, dueDate, uiState } = this.state;
    this.setState(
      {
        licenseFile: uploaded.key,
        licenseFileName: uploaded.name,
        licenseTouched: true,
        licenseUploading: false,
        licenseProgress: 100
      },
      () => this.emitChange(uiState, createDate, dueDate)
    );
  };

  onLicenseUploadError = () => {
    const { intl } = this.props;
    this.setState({
      licenseUploading: false,
      licenseProgress: 0,
      licenseFileName: null
    });
    sendNotification(
      "error",
      intl,
      intl.formatMessage({ id: "rights_license_upload_error" })
    );
  };

  handleRemoveLicenseFile = () => {
    const { createDate, dueDate, uiState } = this.state;
    this.setState(
      {
        licenseFile: null,
        licenseFileName: null,
        licenseTouched: true,
        licenseProgress: 0
      },
      () => this.emitChange(uiState, createDate, dueDate)
    );
  };

  renderLicensePicker() {
    const { intl } = this.props;
    const {
      licenseFile,
      licenseFileName,
      licenseUploading,
      licenseProgress
    } = this.state;

    return (
      <div className="bb-copyright__license">
        <div className="bb-copyright__license-label">
          {intl.formatMessage({ id: "rights_license_label" })}
        </div>

        <input
          type="file"
          ref={this.licenseInputRef}
          style={{ display: "none" }}
          accept={LICENSE_FILE_EXTENSIONS.map(e => `.${e}`).join(",")}
          onChange={this.handleSelectLicenseFile}
        />

        {licenseUploading && (
          <div className="bb-copyright__license-file">
            <span className="bb-copyright__license-name">
              {licenseFileName}
            </span>
            <Progress
              percent={licenseProgress}
              size="small"
              showInfo={false}
              strokeWidth={6}
            />
          </div>
        )}

        {!licenseUploading && licenseFile && (
          <div className="bb-copyright__license-file">
            <Icon type="paper-clip" />
            <Tooltip title={licenseFileName || ""}>
              <span className="bb-copyright__license-name">
                {licenseFileName ||
                  intl.formatMessage({ id: "rights_license_attached" })}
              </span>
            </Tooltip>
            <button
              type="button"
              className="bb-copyright__license-remove"
              onClick={this.handleRemoveLicenseFile}
            >
              {intl.formatMessage({ id: "rights_license_remove" })}
            </button>
          </div>
        )}

        {!licenseUploading && !licenseFile && (
          <button
            type="button"
            className="bb-copyright__license-button"
            onClick={this.handleClickLicense}
          >
            <Icon type="paper-clip" />{" "}
            {intl.formatMessage({ id: "rights_license_attach" })}
          </button>
        )}

        <div className="bb-copyright__license-hint">
          {intl.formatMessage({ id: "rights_license_hint" })}
        </div>
      </div>
    );
  }

  handleUiStateChange = e => {
    const uiState = e.target.value;
    const { createDate, dueDate } = this.state;

    this.setState({ uiState }, () => {
      this.emitChange(uiState, createDate, dueDate);
    });
  };

  render() {
    const {
      intl,
      width,
      rightsAssignedByName,
      rightsAssignedAt,
      allowLicenseFile
    } = this.props;
    const { createDate, dueDate, uiState } = this.state;
    const isMobile = width ? (width <= 578 ? true : false) : false;
    const simple = this.esVersionSimple();
    // En la versión simple el inicio tampoco puede quedar después del fin:
    // el backend rechaza el rango invertido y acá no hay otro aviso.
    const disabledCreateDate =
      simple && dueDate
        ? date => date.isAfter(moment(dueDate, "YYYY-MM-DD"), "day")
        : undefined;

    return (
      <div>
        <div className="bb-copyright__head">
          {intl.formatMessage({ id: "copyright_image_use_rights" })}
        </div>

        <div style={{ marginTop: 10, marginBottom: 10 }}>
          {simple ? (
            <Radio.Group onChange={this.handleUiStateChange} value={uiState}>
              <Radio value={UI_UNASSIGNED}>
                {intl.formatMessage({ id: "undefined" })}
              </Radio>
              <Radio value={UI_DUE_DATE}>
                {intl.formatMessage({ id: "set_duration" })}
              </Radio>
            </Radio.Group>
          ) : (
            <Radio.Group onChange={this.handleUiStateChange} value={uiState}>
              <Radio value={UI_UNASSIGNED}>
                {intl.formatMessage({ id: "rights_unassigned" })}
                <span className="bb-copyright__hint">
                  {intl.formatMessage({ id: "rights_unassigned_hint" })}
                </span>
              </Radio>
              <Radio value={UI_NOT_REQUIRED}>
                {intl.formatMessage({ id: "rights_not_required" })}
                <span className="bb-copyright__hint">
                  {intl.formatMessage({ id: "rights_not_required_hint" })}
                </span>
              </Radio>
              <Radio value={UI_NO_DUE_DATE}>
                {intl.formatMessage({ id: "rights_enabled_no_due" })}
                <span className="bb-copyright__hint">
                  {intl.formatMessage({ id: "rights_enabled_no_due_hint" })}
                </span>
              </Radio>
              <Radio value={UI_DUE_DATE}>
                {intl.formatMessage({ id: "rights_enabled_due" })}
              </Radio>
            </Radio.Group>
          )}
        </div>

        {uiState === UI_DUE_DATE && isMobile && (
          <div className="bb-copyright__range">
            <span>{intl.formatMessage({ id: "copyright_creation_date" })}</span>
            <div>
              <DatePicker
                className="bb-copyright__range-field"
                placeholder={intl.formatMessage({
                  id: "copyright_date_picker"
                })}
                onChange={this.handleOnChangeCreateDate}
                value={createDate ? moment(createDate, "YYYY-MM-DD") : null}
                allowClear={false}
                disabledDate={disabledCreateDate}
              />
            </div>
            <span>{intl.formatMessage({ id: "copyright_due_date" })}</span>
            <div>
              <DatePicker
                className="bb-copyright__range-field"
                placeholder={intl.formatMessage({
                  id: "copyright_date_picker"
                })}
                onChange={this.handleOnChangeDueDate}
                value={dueDate ? moment(dueDate, "YYYY-MM-DD") : null}
                allowClear={false}
                disabledDate={date =>
                  createDate &&
                  date.isBefore(moment(createDate, "YYYY-MM-DD"), "day")
                }
              />
            </div>
          </div>
        )}
        {uiState === UI_DUE_DATE && !isMobile && (
          /* Las dos fechas iban sin rótulo, separadas por una flecha. La de
             inicio viene con hoy puesto y la de vencimiento vacía, así que se
             leía como un solo campo ya completo y el vencimiento se pasaba por
             alto. */
          <div className="bb-copyright__range bb-copyright__range--inline">
            <label className="bb-copyright__range-label">
              <span>{intl.formatMessage({ id: "copyright_creation_date" })}</span>
              <DatePicker
                className="bb-copyright__range-field"
                placeholder={intl.formatMessage({ id: "copyright_date_picker" })}
                onChange={this.handleOnChangeCreateDate}
                value={createDate ? moment(createDate, "YYYY-MM-DD") : null}
                allowClear={false}
                disabledDate={disabledCreateDate}
              />
            </label>
            <span className="bb-copyright__range-arrow">→</span>
            <label className="bb-copyright__range-label">
              <span>{intl.formatMessage({ id: "copyright_due_date" })}</span>
            <DatePicker
              className="bb-copyright__range-field"
              placeholder={intl.formatMessage({ id: "copyright_date_picker" })}
              onChange={this.handleOnChangeDueDate}
              value={dueDate ? moment(dueDate, "YYYY-MM-DD") : null}
              allowClear={false}
              disabledDate={date =>
                createDate &&
                date.isBefore(moment(createDate, "YYYY-MM-DD"), "day")
              }
            />
            </label>
          </div>
        )}

        {uiState === UI_DUE_DATE && !dueDate && (
          <div className="bb-copyright__missing-due">
            {intl.formatMessage({ id: "rights_due_date_required" })}
          </div>
        )}

        {/* El contrato es del módulo; la versión simple es sólo el período. */}
        {!simple &&
          uiState === UI_DUE_DATE &&
          allowLicenseFile &&
          this.renderLicensePicker()}

        {rightsAssignedByName && rightsAssignedAt && (
          <div className="bb-copyright__trace">
            {intl.formatMessage(
              { id: "rights_assigned_trace" },
              {
                name: rightsAssignedByName,
                date: moment(rightsAssignedAt).format("DD/MM/YYYY"),
                time: moment(rightsAssignedAt).format("HH:mm")
              }
            )}
          </div>
        )}
        <br />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  width: state.app.width,
  // Para decidir entre el módulo y la versión simple. La versión sin conectar
  // lo recibe por prop; ver esVersionSimple().
  modules: state.client.data ? state.client.data.modules : [],
  // Contexto que necesita UploadFile para el contrato. Mismo origen que usa
  // CommentEditInput para los adjuntos de comentarios.
  user: state.authUser.user,
  clientId: state.client.data ? state.client.data._id : null,
  clientName: state.client.data ? state.client.data.name : ""
});

const mapDispatchToProps = {};

export default injectIntl(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(Copyright))
);
